import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { type Observable } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { TRANSLOCO_LANG_STORAGE_KEY } from '../../../app.config';
import { AuthService } from '../../../core/services/auth.service';
import { MobileMenuService } from '../../../core/services/mobile-menu.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    TranslocoPipe,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent {
  private readonly authService = inject(AuthService);
  private readonly mobileMenuService = inject(MobileMenuService);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  titleService = inject(Title);

  readonly availableLangs = [
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
  ] as const;
  readonly activeLang$: Observable<string> = this.transloco.langChanges$.pipe(
    startWith(this.transloco.getActiveLang()),
  );

  readonly authenticated = computed(() => this.authService.isLoggedIn());

  readonly userProfile = computed(() => {
    const profile = this.authService.getUserProfile();
    if (!profile) {
      return null;
    }
    return {
      avatar: null as string | null,
      initials: profile.initials,
      name: profile.name,
      email: profile.email,
    };
  });

  constructor() {
    this.titleService.setTitle(environment.appName);
  }

  toggleMobileMenu(): void {
    this.mobileMenuService.toggleMobileMenu();
  }

  onProfileClick(): void {
    this.router.navigate(['/profile']);
  }

  onSettingsClick(): void {
    this.router.navigate(['/settings']);
  }

  async onLogout(): Promise<void> {
    await this.authService.logout();
  }

  setLanguage(lang: string): void {
    this.transloco.setActiveLang(lang);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(TRANSLOCO_LANG_STORAGE_KEY, lang);
    }
  }
}
